import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useAuth } from '@/contexts/AuthContext';
import { useLocale } from '@/contexts/LocaleContext';
import { 
  getConversations, 
  getConversation, 
  createConversation, 
  sendConversationMessage,
  getCompanies 
} from '@/lib/api';
import { 
  MessageSquare, 
  Send,
  Building2,
  Plus,
  ArrowLeft,
  Search,
  Clock,
  CheckCheck,
  Loader2
} from 'lucide-react';
import { toast } from 'sonner';

const MessageList = () => {
  const { conversationId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { isUSA } = useLocale();
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [newMessage, setNewMessage] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  const [showNewConversationDialog, setShowNewConversationDialog] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [newConversationData, setNewConversationData] = useState({
    recipient_company_id: '',
    subject: '',
    initial_message: ''
  });
  const [creatingConversation, setCreatingConversation] = useState(false);
  const messagesEndRef = useRef(null);
  const userCompanyId = user?.company_id;

  useEffect(() => {
    fetchConversations();
    fetchCompanies();
  }, []);

  useEffect(() => {
    if (conversationId) {
      fetchConversation(conversationId);
    } else {
      setSelectedConversation(null);
      setMessages([]);
    }
  }, [conversationId]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchConversations = async () => {
    try {
      const data = await getConversations();
      setConversations(data);
    } catch (error) {
      console.error('Error fetching conversations:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchConversation = async (id) => {
    setLoadingMessages(true);
    try {
      const data = await getConversation(id);
      setSelectedConversation(data.conversation);
      setMessages(data.messages);
      // Update unread count in list
      setConversations(prev => 
        prev.map(c => c.conversation_id === id ? { ...c, unread_count: 0 } : c)
      );
    } catch (error) {
      console.error('Error fetching conversation:', error);
      toast.error('Error al cargar la conversación');
    } finally {
      setLoadingMessages(false);
    }
  };

  const fetchCompanies = async () => {
    try {
      const data = await getCompanies({ limit: 100 });
      // Filter out user's own company
      setCompanies(data.filter(c => c.company_id !== userCompanyId));
    } catch (error) {
      console.error('Error fetching companies:', error);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !conversationId) return;

    setSendingMessage(true);
    try {
      const response = await sendConversationMessage(conversationId, {
        content: newMessage
      });
      setMessages(prev => [...prev, response]);
      setNewMessage('');
      
      // Update conversation list
      setConversations(prev => 
        prev.map(c => c.conversation_id === conversationId 
          ? { ...c, last_message: newMessage.substring(0, 100), last_message_at: new Date().toISOString() }
          : c
        )
      );
    } catch (error) {
      toast.error(isUSA ? 'Error sending message' : 'Error al enviar mensaje');
    } finally {
      setSendingMessage(false);
    }
  };

  const handleCreateConversation = async (e) => {
    e.preventDefault();
    if (!newConversationData.recipient_company_id || !newConversationData.initial_message) {
      toast.error(isUSA ? 'Complete all required fields' : 'Completa todos los campos requeridos');
      return;
    }

    setCreatingConversation(true);
    try {
      const response = await createConversation(newConversationData);
      toast.success(isUSA ? 'Conversation started' : 'Conversación iniciada');
      setShowNewConversationDialog(false);
      setNewConversationData({ recipient_company_id: '', subject: '', initial_message: '' });
      await fetchConversations();
      navigate(`/dashboard/messages/${response.conversation_id}`);
    } catch (error) {
      toast.error(error.message || (isUSA ? 'Error creating conversation' : 'Error al crear conversación'));
    } finally {
      setCreatingConversation(false);
    }
  };

  const getOtherCompanyName = (conv) => {
    if (!conv) return isUSA ? 'Company' : 'Empresa';
    return conv.company_a_id === userCompanyId ? conv.company_b_name : conv.company_a_name;
  };

  const formatTime = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    
    if (days === 0) {
      return date.toLocaleTimeString(isUSA ? 'en-US' : 'es-MX', { hour: '2-digit', minute: '2-digit' });
    } else if (days === 1) {
      return isUSA ? 'Yesterday' : 'Ayer';
    } else if (days < 7) {
      return date.toLocaleDateString(isUSA ? 'en-US' : 'es-MX', { weekday: 'short' });
    } else {
      return date.toLocaleDateString(isUSA ? 'en-US' : 'es-MX', { day: '2-digit', month: 'short' });
    }
  };

  const filteredCompanies = companies.filter(c => 
    c.company_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    c.industry?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div data-testid="messages-page" className="h-[calc(100vh-180px)] lg:h-[calc(100vh-120px)]">
      <div className="flex flex-col lg:flex-row h-full gap-4">
        {/* Conversations List - Hidden on mobile when viewing a conversation */}
        <div className={`lg:w-80 flex-shrink-0 ${conversationId ? 'hidden lg:flex' : 'flex'} flex-col h-full`}>
          <div className="flex items-center justify-between mb-4">
            <h1 className="page-title">{isUSA ? 'Messages' : 'Mensajes'}</h1>
            <Dialog open={showNewConversationDialog} onOpenChange={setShowNewConversationDialog}>
              <DialogTrigger asChild>
                <Button 
                  size="sm" 
                  className="hover:opacity-90"
                  style={{ backgroundColor: '#34B772' }}
                  data-testid="new-conversation-btn"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  {isUSA ? 'New' : 'Nuevo'}
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-md">
                <DialogHeader>
                  <DialogTitle>{isUSA ? 'New conversation' : 'Nueva conversación'}</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleCreateConversation} className="space-y-4">
                  <div className="space-y-2">
                    <Label>{isUSA ? 'Search company' : 'Buscar empresa'}</Label>
                    <Input
                      placeholder={isUSA ? 'Type to search...' : 'Escribe para buscar...'}
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                    />
                    {searchQuery && filteredCompanies.length > 0 && (
                      <div className="max-h-40 overflow-y-auto border rounded-lg">
                        {filteredCompanies.slice(0, 5).map(company => (
                          <button
                            key={company.company_id}
                            type="button"
                            className={`w-full text-left p-3 hover:bg-slate-50 border-b last:border-b-0 ${
                              newConversationData.recipient_company_id === company.company_id ? 'bg-slate-100' : ''
                            }`}
                            onClick={() => {
                              setNewConversationData(prev => ({ ...prev, recipient_company_id: company.company_id }));
                              setSearchQuery(company.company_name);
                            }}
                          >
                            <p className="font-medium text-slate-900">{company.company_name}</p>
                            <p className="text-xs text-slate-500">{company.industry}</p>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="subject">{isUSA ? 'Subject' : 'Asunto'}</Label>
                    <Input
                      id="subject"
                      placeholder={isUSA ? 'Ex: Fabric inquiry' : 'Ej: Consulta sobre telas'}
                      value={newConversationData.subject}
                      onChange={(e) => setNewConversationData(prev => ({ ...prev, subject: e.target.value }))}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="initial_message">{isUSA ? 'Message' : 'Mensaje'}</Label>
                    <Textarea
                      id="initial_message"
                      placeholder={isUSA ? 'Write your message...' : 'Escribe tu mensaje...'}
                      rows={4}
                      value={newConversationData.initial_message}
                      onChange={(e) => setNewConversationData(prev => ({ ...prev, initial_message: e.target.value }))}
                      required
                    />
                  </div>
                  <div className="flex justify-end gap-2">
                    <Button type="button" variant="outline" onClick={() => setShowNewConversationDialog(false)}>
                      {isUSA ? 'Cancel' : 'Cancelar'}
                    </Button>
                    <Button 
                      type="submit" 
                      disabled={creatingConversation || !newConversationData.recipient_company_id}
                      style={{ backgroundColor: '#34B772' }}
                      className="hover:opacity-90"
                    >
                      {creatingConversation ? <Loader2 className="w-4 h-4 animate-spin" /> : (isUSA ? 'Send' : 'Enviar')}
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          {/* Conversations */}
          <div className="flex-1 overflow-y-auto space-y-2">
            {conversations.length === 0 ? (
              <div className="text-center py-12 text-slate-500">
                <MessageSquare className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                <p className="font-medium">{isUSA ? 'No conversations' : 'No tienes conversaciones'}</p>
                <p className="text-sm mt-1">{isUSA ? 'Start a new conversation' : 'Inicia una nueva conversación'}</p>
              </div>
            ) : (
              conversations.map((conv) => (
                <Link
                  key={conv.conversation_id}
                  to={`/dashboard/messages/${conv.conversation_id}`}
                  className={`block p-3 rounded-lg transition-colors ${
                    conversationId === conv.conversation_id 
                      ? 'bg-slate-100' 
                      : 'hover:bg-slate-50'
                  } ${conv.unread_count > 0 ? 'border-l-4 border-l-[#34B772]' : ''}`}
                  data-testid={`conversation-${conv.conversation_id}`}
                >
                  <div className="flex items-start gap-3">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                      conv.unread_count > 0 ? 'bg-[#34B772]/10' : 'bg-slate-100'
                    }`}>
                      <Building2 className={`w-5 h-5 ${conv.unread_count > 0 ? 'text-[#34B772]' : 'text-slate-400'}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <span className={`font-medium text-sm truncate ${conv.unread_count > 0 ? 'text-slate-900' : 'text-slate-700'}`}>
                          {getOtherCompanyName(conv)}
                        </span>
                        <span className="text-xs text-slate-500 flex-shrink-0 ml-2">
                          {formatTime(conv.last_message_at)}
                        </span>
                      </div>
                      <p className="text-xs text-slate-500 truncate">{conv.subject}</p>
                      <p className={`text-sm truncate ${conv.unread_count > 0 ? 'text-slate-900 font-medium' : 'text-slate-500'}`}>
                        {conv.last_message}
                      </p>
                    </div>
                    {conv.unread_count > 0 && (
                      <Badge 
                        className="text-white text-xs flex-shrink-0"
                        style={{ backgroundColor: '#34B772' }}
                      >
                        {conv.unread_count}
                      </Badge>
                    )}
                  </div>
                </Link>
              ))
            )}
          </div>
        </div>

        {/* Chat Area */}
        <div className={`flex-1 flex flex-col bg-white rounded-xl border border-slate-200 overflow-hidden ${!conversationId ? 'hidden lg:flex' : 'flex'}`}>
          {conversationId && selectedConversation ? (
            <>
              {/* Chat Header */}
              <div className="p-4 border-b border-slate-200 flex items-center gap-3 bg-white">
                <Button
                  variant="ghost"
                  size="icon"
                  className="lg:hidden"
                  onClick={() => navigate('/dashboard/messages')}
                >
                  <ArrowLeft className="w-5 h-5" />
                </Button>
                <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: '#1E4C8E' }}>
                  <Building2 className="w-5 h-5 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-slate-900 truncate">
                    {getOtherCompanyName(selectedConversation)}
                  </h3>
                  <p className="text-xs text-slate-500 truncate">{selectedConversation.subject}</p>
                </div>
              </div>

              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-slate-50">
                {loadingMessages ? (
                  <div className="flex items-center justify-center h-full">
                    <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
                  </div>
                ) : messages.length === 0 ? (
                  <div className="text-center py-12 text-slate-500">
                    <p>{isUSA ? 'No messages yet' : 'No hay mensajes aún'}</p>
                  </div>
                ) : (
                  messages.map((msg) => {
                    const isOwn = msg.sender_company_id === userCompanyId;
                    return (
                      <div
                        key={msg.message_id}
                        className={`flex ${isOwn ? 'justify-end' : 'justify-start'}`}
                      >
                        <div
                          className={`max-w-[80%] lg:max-w-[60%] rounded-2xl px-4 py-2 ${
                            isOwn 
                              ? 'bg-[#1E4C8E] text-white rounded-br-md' 
                              : 'bg-white border border-slate-200 text-slate-900 rounded-bl-md'
                          }`}
                        >
                          {!isOwn && msg.sender_user_name && (
                            <p className="text-xs font-medium text-slate-500 mb-1">
                              {msg.sender_user_name}
                            </p>
                          )}
                          <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                          <div className={`flex items-center justify-end gap-1 mt-1 ${isOwn ? 'text-blue-200' : 'text-slate-400'}`}>
                            <span className="text-xs">
                              {formatTime(msg.created_at)}
                            </span>
                            {isOwn && <CheckCheck className="w-3 h-3" />}
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
                <div ref={messagesEndRef} />
              </div>

              {/* Input */}
              <form onSubmit={handleSendMessage} className="p-4 border-t border-slate-200 bg-white">
                <div className="flex items-center gap-2">
                  <Input
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    placeholder={isUSA ? 'Write a message...' : 'Escribe un mensaje...'}
                    className="flex-1"
                    disabled={sendingMessage}
                    data-testid="message-input"
                  />
                  <Button 
                    type="submit" 
                    size="icon"
                    disabled={sendingMessage || !newMessage.trim()}
                    style={{ backgroundColor: '#34B772' }}
                    className="hover:opacity-90"
                    data-testid="send-message-btn"
                  >
                    {sendingMessage ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Send className="w-4 h-4" />
                    )}
                  </Button>
                </div>
              </form>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center text-slate-500">
              <div className="text-center">
                <MessageSquare className="w-16 h-16 mx-auto mb-4 text-slate-300" />
                <p className="font-medium">{isUSA ? 'Select a conversation' : 'Selecciona una conversación'}</p>
                <p className="text-sm mt-1">{isUSA ? 'or start a new one' : 'o inicia una nueva'}</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MessageList;
